import React, { useState, useEffect, useCallback } from 'react';
import { Tag, Button, Input, Select, message, Tooltip, Modal, Descriptions, Typography, Dropdown } from 'antd';
import { 
  SearchOutlined, 
  EyeOutlined, 
  DownloadOutlined, 
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilePdfOutlined,
  CheckOutlined,
  CloseOutlined,
  SyncOutlined,
  MoreOutlined
} from '@ant-design/icons';
import VolunteerRegistrationService from '../../services/VolunteerRegistrationService';
import { formatRelativeTime } from '../../utils/time';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  FilterBar,
  StyledTable,
  StatsRow,
  StatCard,
  SignaturePreview,
  AcknowledgmentList,
  AcknowledgmentItem
} from './styled';

const { Option } = Select;
const { Title, Text } = Typography;

const VolunteerRegistrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  // Fetch registrations
  const fetchRegistrations = useCallback(async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        ...(statusFilter !== 'all' && { status: statusFilter })
      };
      
      const response = await VolunteerRegistrationService.getAllRegistrations(params);
      
      if (response.success) {
        setRegistrations(response.data || []);
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            current: response.pagination.page,
            total: response.pagination.total
          }));
        }
        
        // Calculate stats from data
        const data = response.data || [];
        setStats({
          total: response.pagination?.total || data.length,
          pending: data.filter(r => r.status === 'pending').length,
          approved: data.filter(r => r.status === 'approved').length,
          rejected: data.filter(r => r.status === 'rejected').length
        });
      } else {
        message.error(response.message || 'Failed to fetch registrations');
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      message.error('Failed to fetch registrations');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRegistrations(pagination.current, pagination.pageSize);
  }, [fetchRegistrations, pagination.current, pagination.pageSize]);

  // Handle table change
  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  // View registration details
  const handleViewDetails = async (record) => {
    try {
      const response = await VolunteerRegistrationService.getRegistrationById(record._id);
      if (response.success) {
        setSelectedRegistration(response.data);
        setDetailModalVisible(true);
      } else {
        message.error('Failed to load registration details');
      }
    } catch (error) {
      console.error('Error loading details:', error);
      message.error('Failed to load registration details');
    }
  };

  // Download PDF
  const handleDownloadPdf = (record) => {
    if (!record._id || !record.downloadToken) {
      message.error('Unable to download PDF');
      return;
    }
    
    const url = VolunteerRegistrationService.getPdfDownloadUrl(record._id, record.downloadToken);
    window.open(url, '_blank');
  };

  // Approve volunteer
  const handleApprove = async (record) => {
    try {
      const response = await VolunteerRegistrationService.updateStatus(record._id, 'approved');
      if (response.success) {
        message.success(`${record.fullName} has been approved!`);
        fetchRegistrations(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || 'Failed to approve volunteer');
      }
    } catch (error) {
      console.error('Error approving volunteer:', error);
      message.error('Failed to approve volunteer');
    }
  };

  // Reject volunteer
  const handleReject = async (record) => {
    try {
      const response = await VolunteerRegistrationService.updateStatus(record._id, 'rejected');
      if (response.success) {
        message.success(`${record.fullName} has been rejected`);
        fetchRegistrations(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || 'Failed to reject volunteer');
      }
    } catch (error) {
      console.error('Error rejecting volunteer:', error);
      message.error('Failed to reject volunteer');
    }
  };

  // Filter registrations by search
  const filteredRegistrations = registrations.filter(reg => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      reg.fullName?.toLowerCase().includes(search) ||
      reg.email?.toLowerCase().includes(search) ||
      reg.preferredCity?.toLowerCase().includes(search)
    );
  });

  // Sync approved volunteers to user accounts
  const [syncing, setSyncing] = useState(false);
  const handleSyncVolunteers = async () => {
    try {
      setSyncing(true);
      const response = await VolunteerRegistrationService.syncApprovedToUsers();
      if (response.success) {
        const { created, upgraded, skipped } = response.data || {};
        message.success(response.message || `Sync complete: ${created} created, ${upgraded} upgraded, ${skipped} already synced`);
      } else {
        message.error(response.message || 'Failed to sync volunteers');
      }
    } catch (error) {
      console.error('Error syncing volunteers:', error);
      message.error('Failed to sync volunteers');
    } finally {
      setSyncing(false);
    }
  };

  // Confirmation wrapper for approve
  const confirmApprove = (record) => {
    Modal.confirm({
      title: 'Approve Volunteer',
      content: `Approve ${record.fullName}${record.preferredCity ? ` for ${record.preferredCity}` : ''}?`,
      okText: 'Approve',
      okButtonProps: { style: { backgroundColor: '#52c41a', borderColor: '#52c41a' } },
      onOk: () => handleApprove(record),
    });
  };

  // Confirmation wrapper for reject
  const confirmReject = (record) => {
    Modal.confirm({
      title: 'Reject Volunteer',
      content: `Reject ${record.fullName}'s application?`,
      okText: 'Reject',
      okButtonProps: { danger: true },
      onOk: () => handleReject(record),
    });
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: (a, b) => a.fullName.localeCompare(b.fullName),
      render: (text, record) => (
        <div>
          <strong>{text}</strong>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>
        </div>
      )
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <div>
          <div>
            {record.preferredCity 
              ? `${record.preferredCity}, ${record.preferredState || ''}`.trim()
              : (record.isGeneralVolunteer ? 'Any Location' : '-')
            }
          </div>
          {record.selectedEventName && (
            <div style={{ fontSize: '12px', color: '#4A90E2', marginTop: 2 }}>
              Event: {record.selectedEventName}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Volunteer Area',
      dataIndex: 'volunteerArea',
      key: 'volunteerArea',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Medical Experience',
      dataIndex: 'medicalExperience',
      key: 'medicalExperience',
      render: (text) => (
        <Tag color={text === 'No Medical Experience' ? 'default' : 'green'}>
          {text}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          pending: 'gold',
          approved: 'green',
          rejected: 'red',
          'checked-in': 'blue',
          completed: 'purple',
          cancelled: 'default'
        };
        return <Tag color={colors[status] || 'default'}>{status?.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Waiver Signed',
      key: 'waiverSigned',
      render: (_, record) => (
        record.waiverSignedAt ? (
          <Tooltip title={`Signed ${formatRelativeTime(record.waiverSignedAt)}`}>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
          </Tooltip>
        ) : (
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
        )
      )
    },
    {
      title: 'Registered',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (date) => formatRelativeTime(date)
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      align: 'center',
      render: (_, record) => {
        const items = [
          ...(record.status === 'pending' ? [
            {
              key: 'approve',
              label: 'Approve',
              icon: <CheckOutlined style={{ color: '#52c41a' }} />,
            },
            {
              key: 'reject',
              label: 'Reject',
              icon: <CloseOutlined />,
              danger: true,
            },
            { type: 'divider' },
          ] : []),
          {
            key: 'view',
            label: 'View Details',
            icon: <EyeOutlined />,
          },
          {
            key: 'download',
            label: 'Download Waiver',
            icon: <DownloadOutlined />,
          },
        ];

        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key }) => {
                switch (key) {
                  case 'approve': confirmApprove(record); break;
                  case 'reject': confirmReject(record); break;
                  case 'view': handleViewDetails(record); break;
                  case 'download': handleDownloadPdf(record); break;
                  default: break;
                }
              }
            }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined style={{ fontSize: 18 }} />} style={{ height: 32, width: 32, padding: 0 }} />
          </Dropdown>
        );
      }
    }
  ];

  // Render acknowledgment status
  const renderAcknowledgments = (registration) => {
    const acknowledgments = [
      { key: 'acknowledgedRisks', label: 'Acknowledged Risks' },
      { key: 'acknowledgedLiability', label: 'Acknowledged Liability' },
      { key: 'acknowledgedSafetyInstructions', label: 'Acknowledged Safety Instructions' },
      { key: 'acknowledgedEmergencyAuth', label: 'Authorized Emergency Care' },
      { key: 'acknowledgedMedicalExpenses', label: 'Acknowledged Medical Expenses' }
    ];

    // If the single acknowledgedWaiver field is true, treat all as acknowledged
    const waiverCoverAll = registration.acknowledgedWaiver === true;

    return (
      <AcknowledgmentList>
        {acknowledgments.map(ack => {
          const isAcknowledged = registration[ack.key] || waiverCoverAll;
          return (
            <AcknowledgmentItem key={ack.key} $acknowledged={isAcknowledged}>
              {isAcknowledged ? (
                <CheckCircleOutlined className="icon" />
              ) : (
                <CloseCircleOutlined className="icon" />
              )}
              <span>{ack.label}</span>
            </AcknowledgmentItem>
          );
        })}
      </AcknowledgmentList>
    );
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Volunteer Registrations</PageTitle>
      </PageHeader>

      <StatsRow>
        <StatCard $color="#4A90E2">
          <h3>{stats.total}</h3>
          <p>Total Registrations</p>
        </StatCard>
        <StatCard $color="#f5a623">
          <h3>{stats.pending}</h3>
          <p>Pending Review</p>
        </StatCard>
        <StatCard $color="#52c41a">
          <h3>{stats.approved}</h3>
          <p>Approved</p>
        </StatCard>
        <StatCard $color="#ff4d4f">
          <h3>{stats.rejected}</h3>
          <p>Rejected</p>
        </StatCard>
      </StatsRow>

      <FilterBar>
        <Input
          placeholder="Search by name, email, or event..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300, height: 40 }}
          allowClear
        />
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 150, height: 40 }}
        >
          <Option value="all">All Statuses</Option>
          <Option value="pending">Pending</Option>
          <Option value="approved">Approved</Option>
          <Option value="rejected">Rejected</Option>
          <Option value="checked-in">Checked In</Option>
          <Option value="cancelled">Cancelled</Option>
        </Select>
        <Tooltip title="Sync approved registrations to volunteer user accounts">
          <Button
            icon={<SyncOutlined spin={syncing} />}
            onClick={handleSyncVolunteers}
            loading={syncing}
            style={{ height: 40 }}
          >
            Sync to Users
          </Button>
        </Tooltip>
      </FilterBar>

      <StyledTable
        columns={columns}
        dataSource={filteredRegistrations}
        rowKey="_id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} registrations`
        }}
        onChange={handleTableChange}
      />

      {/* Detail Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FilePdfOutlined style={{ color: '#e74c3c' }} />
            <span>Volunteer Registration Details</span>
          </div>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={700}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          selectedRegistration && (
            <Button 
              key="download" 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadPdf(selectedRegistration)}
            >
              Download Waiver
            </Button>
          )
        ]}
      >
        {selectedRegistration && (
          <>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Full Name" span={2}>
                <strong>{selectedRegistration.fullName}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedRegistration.email}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedRegistration.phoneNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Location" span={2}>
                {selectedRegistration.preferredCity 
                  ? `${selectedRegistration.preferredCity}, ${selectedRegistration.preferredState || ''}`.trim()
                  : (selectedRegistration.isGeneralVolunteer ? 'Any Location' : '-')
                }
              </Descriptions.Item>
              {selectedRegistration.selectedEventName && (
                <Descriptions.Item label="Selected Event" span={2}>
                  <div>
                    <strong>{selectedRegistration.selectedEventName}</strong>
                    {selectedRegistration.selectedEventDate && (
                      <span style={{ marginLeft: 8, color: '#666' }}>
                        ({new Date(selectedRegistration.selectedEventDate).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })})
                      </span>
                    )}
                  </div>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Volunteer Area">
                <Tag color="blue">{selectedRegistration.volunteerArea}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Medical Experience">
                <Tag color="green">{selectedRegistration.medicalExperience}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={
                  selectedRegistration.status === 'confirmed' ? 'blue' :
                  selectedRegistration.status === 'checked-in' ? 'green' :
                  selectedRegistration.status === 'cancelled' ? 'red' : 'gold'
                }>
                  {selectedRegistration.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Waiver Version">
                {selectedRegistration.waiverVersion || '1.0'}
              </Descriptions.Item>
              <Descriptions.Item label="Signed At" span={2}>
                {selectedRegistration.signedAt 
                  ? new Date(selectedRegistration.signedAt).toLocaleString()
                  : '-'
                }
              </Descriptions.Item>
              <Descriptions.Item label="Printed Name" span={2}>
                {selectedRegistration.printedName}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginTop: 20, marginBottom: 12 }}>
              Waiver Acknowledgments
            </Title>
            {renderAcknowledgments(selectedRegistration)}

            <Title level={5} style={{ marginTop: 20, marginBottom: 12 }}>
              Signature
            </Title>
            <SignaturePreview>
              {selectedRegistration.signatureType === 'draw' && selectedRegistration.signatureData ? (
                <img 
                  src={selectedRegistration.signatureData} 
                  alt="Signature" 
                />
              ) : selectedRegistration.typedSignature ? (
                <div className="typed-signature">
                  {selectedRegistration.typedSignature}
                </div>
              ) : (
                <Text type="secondary">No signature available</Text>
              )}
            </SignaturePreview>
          </>
        )}
      </Modal>
    </PageContainer>
  );
};

export default VolunteerRegistrations;
